import { useState } from 'react';
import {
  Bell,
  BellOff,
  CheckCircle2,
  Loader2,
  RefreshCw,
  ShieldAlert,
  TriangleAlert,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiErrorBanner } from '@/features/parameters/ApiErrorBanner';

import { useAlerts, useAcknowledgeAlert, useTriggerBatch } from './api';
import { type AlmAlert, type AlertType, ALERT_TYPE_LABELS } from './types';

type FilterType = AlertType | 'ALL';

const TYPE_FILTERS: Array<{ value: FilterType; label: string }> = [
  { value: 'ALL',         label: 'All' },
  { value: 'LCR_BREACH',  label: 'LCR' },
  { value: 'NSFR_BREACH', label: 'NSFR' },
  { value: 'EVE_BREACH',  label: 'EVE' },
  { value: 'NIM_BREACH',  label: 'NIM' },
];

/**
 * Phase 3.1 / 3.4 — ALM Breach Alerts page.
 *
 * Shows LCR, NSFR, EVE (IRRBB outlier) and NIM stress-drop breach alerts
 * generated by the nightly batch scheduler (23:15) or triggered manually.
 */
export function AlertsPage() {
  const [showAll,    setShowAll]    = useState(false);
  const [typeFilter, setTypeFilter] = useState<FilterType>('ALL');

  const query    = useAlerts(showAll);
  const ackMut   = useAcknowledgeAlert();
  const batchMut = useTriggerBatch();

  const allAlerts   = query.data?.alerts ?? [];
  const unreadCount = query.data?.unacknowledgedCount ?? 0;

  const alerts = typeFilter === 'ALL'
    ? allAlerts
    : allAlerts.filter(a => a.alertType === typeFilter);

  const countFor = (t: FilterType) =>
    t === 'ALL' ? allAlerts.length : allAlerts.filter(a => a.alertType === t).length;

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">ALM Alerts</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Breach alerts generated nightly at 23:15 — covers LCR, NSFR,
            EVE (IRRBB supervisory outlier) and NIM stress drop.
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900 dark:text-red-200">
                {unreadCount} unacknowledged
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => setShowAll(v => !v)}>
            {showAll
              ? <><Bell className="mr-1.5 h-3.5 w-3.5" /> Unread only</>
              : <><BellOff className="mr-1.5 h-3.5 w-3.5" /> All history</>
            }
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => batchMut.mutate(undefined)}
            disabled={batchMut.isPending}
          >
            {batchMut.isPending
              ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Running…</>
              : <><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Run batch now</>
            }
          </Button>
        </div>
      </div>

      {/* ── Banners ───────────────────────────────────────────────────── */}
      {query.isError    && <ApiErrorBanner error={query.error} />}
      {batchMut.isError && <ApiErrorBanner error={batchMut.error} />}
      {batchMut.isSuccess && (
        <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          Batch run completed — alerts refreshed.
        </div>
      )}

      {/* ── Type filter pills ─────────────────────────────────────────── */}
      {!query.isLoading && allAlerts.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {TYPE_FILTERS.map(({ value, label }) => {
            const count  = countFor(value);
            const active = typeFilter === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setTypeFilter(value)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  active
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-background text-muted-foreground hover:border-foreground/40 hover:text-foreground'
                }`}
              >
                {label}
                <span className={`rounded-full px-1 text-[10px] ${active ? 'bg-background/20' : 'bg-muted'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Loading skeletons ─────────────────────────────────────────── */}
      {query.isLoading && (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      )}

      {/* ── Empty state ───────────────────────────────────────────────── */}
      {!query.isLoading && alerts.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            <p className="font-medium">
              {typeFilter !== 'ALL'
                ? `No ${ALERT_TYPE_LABELS[typeFilter as AlertType]} alerts`
                : showAll ? 'No alerts on record' : 'No unacknowledged alerts'}
            </p>
            <p className="text-sm text-muted-foreground">
              {typeFilter !== 'ALL'
                ? 'No breaches of this type recorded. Try "All" to see other alert types.'
                : showAll
                ? 'The nightly batch has not detected any LCR, NSFR, EVE, or NIM breaches.'
                : 'All alerts are acknowledged. Toggle "All history" to see past alerts.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* ── Alert table ───────────────────────────────────────────────── */}
      {alerts.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                {showAll ? 'Alert history' : 'Unacknowledged alerts'}
                {typeFilter !== 'ALL' && (
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    · {ALERT_TYPE_LABELS[typeFilter as AlertType]}
                  </span>
                )}
              </CardTitle>
              <span className="text-xs text-muted-foreground">
                {alerts.length} result{alerts.length !== 1 ? 's' : ''}
              </span>
            </div>
            <CardDescription>
              Acknowledge once reviewed and remediation is underway.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-right">Value</th>
                  <th className="px-4 py-2 text-right">Threshold</th>
                  <th className="px-4 py-2 text-left">Severity</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {alerts.map(alert => (
                  <AlertRow
                    key={alert.id}
                    alert={alert}
                    onAck={() => ackMut.mutate(alert.id)}
                    acking={ackMut.isPending && ackMut.variables === alert.id}
                  />
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── AlertRow ──────────────────────────────────────────────────────────────────

function AlertRow({ alert, onAck, acking }: {
  alert:  AlmAlert;
  onAck:  () => void;
  acking: boolean;
}) {
  const isCritical = alert.severity === 'CRITICAL';

  // EVE and NIM use headroom encoding: stored ratio = 1 − actual%.
  // Show the underlying metric pct for legibility.
  const isHeadroom = alert.alertType === 'EVE_BREACH' || alert.alertType === 'NIM_BREACH';
  const displayValue = isHeadroom
    ? `${((1 - alert.ratioValue) * 100).toFixed(1)}%`
    : `${(alert.ratioValue * 100).toFixed(1)}%`;
  const displayThreshold = isHeadroom
    ? `${((1 - alert.threshold) * 100).toFixed(0)}%`
    : `${(alert.threshold * 100).toFixed(0)}%`;
  const valueLabel = isHeadroom
    ? (alert.alertType === 'EVE_BREACH' ? '|EVE/PV|' : 'NIM drop')
    : '';

  return (
    <tr className="border-b last:border-0 transition-colors hover:bg-muted/20">
      <td className="px-4 py-3 font-mono text-xs">{alert.alertDate}</td>
      <td className="px-4 py-3 font-medium">{ALERT_TYPE_LABELS[alert.alertType]}</td>
      <td className="px-4 py-3 text-right">
        <span className={`font-mono text-xs ${isCritical ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
          {displayValue}
        </span>
        {valueLabel && (
          <span className="ml-1 text-[10px] text-muted-foreground">{valueLabel}</span>
        )}
      </td>
      <td className="px-4 py-3 text-right font-mono text-xs text-muted-foreground">
        {displayThreshold}
      </td>
      <td className="px-4 py-3">
        <SeverityBadge severity={alert.severity} />
      </td>
      <td className="px-4 py-3">
        {alert.acknowledged ? (
          <span className="text-xs text-muted-foreground">
            Acked by {alert.acknowledgedBy ?? '—'}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300">
            <TriangleAlert className="h-3 w-3" /> Pending
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {!alert.acknowledged && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onAck}
            disabled={acking}
            className="h-7 px-2 text-xs"
          >
            {acking
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Acknowledge</>
            }
          </Button>
        )}
      </td>
    </tr>
  );
}

function SeverityBadge({ severity }: { severity: 'WARNING' | 'CRITICAL' }) {
  return severity === 'CRITICAL' ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900 dark:text-red-200">
      <ShieldAlert className="h-3 w-3" /> Critical
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900 dark:text-amber-200">
      <TriangleAlert className="h-3 w-3" /> Warning
    </span>
  );
}
